import * as React from 'react';

import { canRunScanner } from '../domains/member-in-event/constants';

/**
 * `MEM-031` AC-1, `PLT-008` AC-4 — can this device run the scanner?
 *
 * Read once, on mount, into state. The three facts are read off the browser here and nowhere else
 * so that {@link canRunScanner} stays a pure function a test can reach — the same split
 * `event-tickets/constants.ts` uses for everything with a decision in it.
 *
 * `mediaDevices` rather than a user-agent string: a UA test is a list that goes out of date, and
 * the question is a capability rather than a brand. **Never a width**: a narrow window on a laptop
 * is not a phone, and a phone held sideways is still a phone.
 *
 * ## Why it is in `src/member/` and not in either slice
 *
 * It was local to `connect-page.tsx` until `MEM-057` put a second scanner entry point on Your
 * account. Both have to answer this question the same way — `responsive-behaviour.ts`'s
 * `DECIDED_BY_DEVICE_NOT_WIDTH` names exactly one gate for the scanner, and two copies of it is
 * how that list quietly becomes untrue. Importing a hook out of a page module would have worked
 * and would have made every screen that wants the gate depend on the Connect page. It is beside
 * the shell for the same reason `pinned-bar-geometry.ts` is: two slices need it and neither owns
 * it.
 */
export function useScannerAvailability(): boolean {
  const [usable, setUsable] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    setUsable(
      canRunScanner({
        hasCamera: typeof navigator.mediaDevices?.getUserMedia === 'function',
        isTouchPrimary: window.matchMedia('(pointer: coarse)').matches,
        isSecureContext: window.isSecureContext,
      }),
    );
  }, []);

  return usable;
}
